/**
 * ═══════════════════════════════════════════════════════════════════════════
 * GLP-1 & related — Spanish market curated dataset
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Fuente única de datos médicos del comparador. Todo dato que aparezca en
 * la UI debe salir de aquí — nunca inventado, nunca generado por IA.
 *
 * Al actualizar cualquier dato médico, incrementar `medical_data_version`
 * y actualizar `last_data_review`.
 *
 * Fuentes oficiales:
 *   - EMA (European Medicines Agency)
 *   - AEMPS · CIMA (fichas técnicas)
 *   - BIFIMED (financiación en el SNS)
 *   - Ensayos pivotales publicados (NEJM, JAMA)
 * ═══════════════════════════════════════════════════════════════════════════
 */

export const MEDICAL_DATA_VERSION = "es-glp1-2026-09-11";
export const LAST_DATA_REVIEW = "2026-09-11"; // Actualizar en cada revisión.
export const LAST_DATA_REVIEW_HUMAN_ES = "11 de septiembre de 2026";

// ───────────────────────── Medications ─────────────────────────
export const MEDICATIONS = [
  {
    id: "wegovy",
    brand_name: "Wegovy",
    active_ingredient: "semaglutida",
    drug_class: "Agonista del receptor GLP-1",
    mvp_formulation: "Solución inyectable",
    administration: {
      route: "subcutánea",
      frequency: "semanal",
      frequency_label_es: "Semanal",
    },
    eu_indication_adults: {
      weight_management: true,
      summary:
        "Control del peso, incluida pérdida y mantenimiento, junto con dieta hipocalórica y aumento de actividad física, en adultos con IMC ≥30 kg/m², o IMC ≥27 y <30 kg/m² con al menos una comorbilidad relacionada con el peso.",
    },
    spain_aemps_indication: {
      weight_management: true,
      source: "https://cima.aemps.es/cima/dochtml/ft/1211608008/FT_1211608008.html",
    },
    prescription_required: true,
    sns_funding: {
      weight_management: "no_financiado",
      detail: "No financiado por el Sistema Nacional de Salud para control del peso.",
      checked_at: LAST_DATA_REVIEW,
      source_type: "bifimed",
    },
    spain_availability: {
      regulatory_status: "autorizado_registrado",
      pharmacy_stock: null,
      display:
        "Autorizado/registrado en España. La disponibilidad en farmacia puede variar.",
    },
    pivotal_evidence: {
      trial: "STEP 1",
      duration_weeks: 68,
      population:
        "Adultos con sobrepeso u obesidad del ensayo STEP 1.",
      intervention_context:
        "Semaglutida 2,4 mg semanal en el contexto del ensayo.",
      results: [
        { arm: "Semaglutida 2,4 mg", weight_change_percent: -14.9 },
        { arm: "Placebo", weight_change_percent: -2.4 },
      ],
      comparator: "placebo",
      reference: "https://doi.org/10.1056/NEJMoa2032183",
      data_source: "publicación primaria + ficha técnica CIMA",
    },
    common_adverse_effects: {
      frequency_band: "Muy frecuentes (≥1/10)",
      items: [
        "dolor de cabeza",
        "náuseas",
        "vómitos",
        "diarrea",
        "estreñimiento",
        "dolor abdominal",
        "sensación de debilidad o cansancio",
      ],
    },
    key_safety_topics: [
      "pancreatitis aguda",
      "deshidratación",
      "retinopatía diabética",
      "retraso del vaciamiento gástrico",
      "reacciones alérgicas graves",
      "NOIANA (neuropatía óptica isquémica anterior no arterítica)",
    ],
    marketing_authorisation_holder: "Novo Nordisk A/S",
    sources: {
      ema: "https://www.ema.europa.eu/en/medicines/human/EPAR/wegovy",
      cima: "https://cima.aemps.es/cima/dochtml/ft/1211608008/FT_1211608008.html",
      bifimed:
        "https://www.sanidad.gob.es/profesionales/medicamentos.do?cn=758089&metodo=verDetalle",
      primary_trial: "https://doi.org/10.1056/NEJMoa2032183",
    },
    last_data_review: LAST_DATA_REVIEW,
  },

  {
    id: "mounjaro",
    brand_name: "Mounjaro",
    active_ingredient: "tirzepatida",
    drug_class: "Agonista dual de los receptores GIP y GLP-1",
    mvp_formulation: "Solución inyectable",
    administration: {
      route: "subcutánea",
      frequency: "semanal",
      frequency_label_es: "Semanal",
    },
    eu_indication_adults: {
      weight_management: true,
      summary:
        "Control del peso, incluida pérdida y mantenimiento, junto con dieta hipocalórica y aumento de actividad física, en adultos con IMC ≥30 kg/m², o IMC ≥27 y <30 kg/m² con al menos una comorbilidad relacionada con el peso.",
    },
    spain_aemps_indication: {
      weight_management: true,
      source: "https://cima.aemps.es/cima/dochtml/ft/1221685041/FT_1221685041.html",
    },
    prescription_required: true,
    sns_funding: {
      weight_management: "no_financiado",
      detail:
        "La presentación consultada figura como no financiada por resolución. En BIFIMED, las indicaciones de control del peso figuran sin petición de financiación. Verificar por presentación en cada actualización del dataset.",
      checked_at: LAST_DATA_REVIEW,
      source_type: "bifimed",
    },
    spain_availability: {
      regulatory_status: "autorizado_registrado",
      pharmacy_stock: null,
      display:
        "Autorizado/registrado en España. La disponibilidad en farmacia puede variar.",
    },
    pivotal_evidence: {
      trial: "SURMOUNT-1",
      duration_weeks: 72,
      population:
        "Adultos con obesidad o sobrepeso y comorbilidad relacionada con el peso, sin diabetes tipo 2 en el ensayo principal.",
      intervention_context:
        "Datos regulatorios de la ficha técnica CIMA correspondientes al ensayo SURMOUNT-1.",
      results: [
        { arm: "Tirzepatida 5 mg", weight_change_percent: -16.0 },
        { arm: "Tirzepatida 10 mg", weight_change_percent: -21.4 },
        { arm: "Tirzepatida 15 mg", weight_change_percent: -22.5 },
        { arm: "Placebo", weight_change_percent: -2.4 },
      ],
      comparator: "placebo",
      reference: "https://doi.org/10.1056/NEJMoa2206038",
      data_source: "publicación primaria + ficha técnica CIMA",
      dose_note:
        "Los porcentajes corresponden a las dosis y al diseño de SURMOUNT-1. No se pueden extrapolar a otros ensayos ni interpretar como recomendación de dosis.",
    },
    common_adverse_effects: {
      frequency_band: "Muy frecuentes (≥1/10)",
      items: [
        "náuseas",
        "diarrea",
        "vómitos",
        "dolor abdominal",
        "estreñimiento",
      ],
    },
    key_safety_topics: [
      "pancreatitis aguda",
      "colelitiasis",
      "colecistitis",
      "retraso del vaciamiento gástrico",
      "hipoglucemia con determinados tratamientos antidiabéticos",
      "reacciones de hipersensibilidad",
    ],
    marketing_authorisation_holder: "Eli Lilly Nederland B.V.",
    sources: {
      ema: "https://www.ema.europa.eu/en/medicines/human/EPAR/mounjaro",
      cima: "https://cima.aemps.es/cima/dochtml/ft/1221685041/FT_1221685041.html",
      bifimed:
        "https://www.sanidad.gob.es/profesionales/medicamentos.do?cn=758637&metodo=verDetalle",
      primary_trial: "https://doi.org/10.1056/NEJMoa2206038",
    },
    last_data_review: LAST_DATA_REVIEW,
  },

  {
    id: "saxenda",
    brand_name: "Saxenda",
    active_ingredient: "liraglutida",
    drug_class: "Agonista del receptor GLP-1",
    mvp_formulation: "Solución inyectable",
    administration: {
      route: "subcutánea",
      frequency: "diaria",
      frequency_label_es: "Diaria",
    },
    eu_indication_adults: {
      weight_management: true,
      summary:
        "Control del peso junto con dieta baja en calorías y aumento de actividad física en adultos con IMC ≥30 kg/m², o IMC ≥27 y <30 kg/m² con al menos una comorbilidad relacionada con el peso.",
    },
    spain_aemps_indication: {
      weight_management: true,
      source: "https://cima.aemps.es/cima/dochtml/ft/115992002/FT_115992002.html",
    },
    prescription_required: true,
    sns_funding: {
      weight_management: "no_financiado",
      detail: "No financiado por el Sistema Nacional de Salud para control del peso.",
      checked_at: LAST_DATA_REVIEW,
      source_type: "bifimed",
    },
    spain_availability: {
      regulatory_status: "autorizado_registrado",
      pharmacy_stock: null,
      display:
        "Autorizado/registrado en España. La disponibilidad en farmacia puede variar.",
    },
    pivotal_evidence: {
      trial: "SCALE Obesity & Prediabetes",
      duration_weeks: 56,
      population:
        "Adultos con obesidad o sobrepeso, algunos con prediabetes, del ensayo SCALE Obesity & Prediabetes.",
      intervention_context:
        "Liraglutida 3,0 mg diaria en el contexto del ensayo.",
      results: [
        { arm: "Liraglutida 3,0 mg", weight_change_percent: -8.0 },
        { arm: "Placebo", weight_change_percent: -2.6 },
      ],
      comparator: "placebo",
      reference: "https://doi.org/10.1056/NEJMoa1411892",
      data_source: "publicación primaria + ficha técnica CIMA",
    },
    common_adverse_effects: {
      frequency_band: "Muy frecuentes (≥1/10)",
      items: [
        "dolor de cabeza",
        "náuseas",
        "vómitos",
        "diarrea",
        "estreñimiento",
      ],
    },
    key_safety_topics: [
      "pancreatitis",
      "colelitiasis",
      "colecistitis",
      "deshidratación",
      "retraso del vaciamiento gástrico",
      "reacción anafiláctica",
    ],
    marketing_authorisation_holder: "Novo Nordisk A/S",
    sources: {
      ema: "https://www.ema.europa.eu/en/medicines/human/EPAR/saxenda",
      cima: "https://cima.aemps.es/cima/dochtml/ft/115992002/FT_115992002.html",
      bifimed:
        "https://www.sanidad.gob.es/profesionales/medicamentos.do?cn=710663&metodo=verDetalle",
      primary_trial: "https://doi.org/10.1056/NEJMoa1411892",
    },
    last_data_review: LAST_DATA_REVIEW,
  },
];

// ───────────────────────── Direct comparisons (head-to-head trials) ─────────────────────────
export const DIRECT_COMPARISONS = [
  {
    pair: ["wegovy", "mounjaro"],
    trial: "SURMOUNT-5",
    duration_label_es: "72 semanas",
    duration_weeks: 72,
    population:
      "Adultos con obesidad sin diabetes tipo 2.",
    design_summary_es:
      "Ensayo aleatorizado fase 3b que comparó las dosis máximas toleradas de tirzepatida y semaglutida administradas una vez por semana.",
    results: [
      { medication_id: "wegovy", arm: "Semaglutida", weight_change_percent: -13.7 },
      { medication_id: "mounjaro", arm: "Tirzepatida", weight_change_percent: -20.2 },
    ],
    reference: "https://doi.org/10.1056/NEJMoa2416394",
  },
  {
    pair: ["wegovy", "saxenda"],
    trial: "STEP 8",
    duration_label_es: "68 semanas",
    duration_weeks: 68,
    population:
      "Adultos con sobrepeso u obesidad sin diabetes.",
    design_summary_es:
      "Ensayo aleatorizado que comparó semaglutida 2,4 mg semanal frente a liraglutida 3,0 mg diaria.",
    results: [
      { medication_id: "wegovy", arm: "Semaglutida 2,4 mg", weight_change_percent: -15.8 },
      { medication_id: "saxenda", arm: "Liraglutida 3,0 mg", weight_change_percent: -6.4 },
    ],
    reference: "https://doi.org/10.1001/jama.2021.23619",
  },
];

// ───────────────────────── Utils ─────────────────────────
export function findMedication(id) {
  return MEDICATIONS.find((m) => m.id === id) || null;
}

export function findDirectComparison(idA, idB) {
  if (!idA || !idB || idA === idB) return null;
  return (
    DIRECT_COMPARISONS.find((dc) => {
      const [x, y] = dc.pair;
      return (x === idA && y === idB) || (x === idB && y === idA);
    }) || null
  );
}

export function pairId(idA, idB) {
  if (!idA || !idB) return null;
  return [idA, idB].sort().join("_vs_");
}
